/**
 * Pure helper functions for reading and formatting ticket data for detail views.
 *
 * Rules:
 * - Pure functions with no Vue instance / this dependencies.
 * - No DOM or $refs manipulation.
 * - No API calls or state mutations.
 */

import { getBuildingLocalizedName } from "./buildingBilingual.js";
import { formatAvailableTimeDisplay } from "../i18n/formatAvailableTime.js";
import { translateSection, translateJobType, translateDepartment } from "./categoryi18n.js";


/**
 * Returns the asset code or empty string.
 */
export function getTicketAssetCode(report) {
  return (report && report.userPart && report.userPart.assetCode) || "";
}

/**
 * Returns problem details or neutral fallback.
 */
export function getTicketProblemDetails(report) {
  return (report && report.userPart && report.userPart.problemDetails) || "-";
}

/**
 * Returns problem image URL/string or null.
 */
export function getTicketProblemImage(report) {
  return (report && report.userPart && report.userPart.problemImage) || null;
}

/**
 * Returns array of problem images (up to 3) or empty array.
 */
export function getTicketProblemImages(report) {
  if (!report || !report.userPart) return [];
  if (Array.isArray(report.userPart.problemImages) && report.userPart.problemImages.length > 0) {
    return report.userPart.problemImages.filter(Boolean);
  }
  return report.userPart.problemImage ? [report.userPart.problemImage] : [];
}

/**
 * Returns maintenance image URL/string or null.
 */
export function getTicketMaintenanceImage(report) {
  return (report && report.technicianPart && report.technicianPart.maintenanceImage) || null;
}

/**
 * Returns array of maintenance images (up to 3) or empty array.
 */
export function getTicketMaintenanceImages(report) {
  if (!report || !report.technicianPart) return [];
  if (Array.isArray(report.technicianPart.maintenanceImages) && report.technicianPart.maintenanceImages.length > 0) {
    return report.technicianPart.maintenanceImages.filter(Boolean);
  }
  return report.technicianPart.maintenanceImage ? [report.technicianPart.maintenanceImage] : [];
}

/**
 * Returns maintenance details text or neutral fallback.
 */
export function getTicketMaintenanceDetails(report) {
  return (report && report.technicianPart && report.technicianPart.maintenanceDetails) || "-";
}

/**
 * Checks if the ticket is for an outdoor location with coordinates.
 */
export function isTicketOutdoor(report) {
  return (
    report &&
    report.userPart &&
    report.userPart.locationType === "outdoor" &&
    !!report.userPart.outdoorLocation
  );
}

/**
 * Returns a human-readable location string.
 * @param {Object} report
 * @param {Function} [t] - Translation function (e.g. this.$t)
 */
export function getTicketLocationFull(report, t, locale) {
  const u = report && report.userPart;
  if (!u) return "-";

  if (u.locationType === "building") {
    const bObj = u.buildingLocation && u.buildingLocation.building;
    let bName =
      bObj &&
      (bObj.name || bObj.BUILDINGCODE);
    const rName = (u.buildingLocation && u.buildingLocation.roomName) || "-";

    if (bName) {
      let cleanRoom = typeof rName === "string" ? rName.trim() : String(rName);
      const bTrimmed = typeof bName === "string" ? bName.trim() : String(bName);
      const bCode = bObj && bObj.BUILDINGCODE ? String(bObj.BUILDINGCODE).trim() : "";
      const bAltName = bObj && bObj.name ? String(bObj.name).trim() : "";

      // 1. If roomName starts with full building name/code (check longer name first)
      if (bAltName && cleanRoom.startsWith(bAltName)) {
        cleanRoom = cleanRoom.substring(bAltName.length);
      } else if (bTrimmed && cleanRoom.startsWith(bTrimmed)) {
        cleanRoom = cleanRoom.substring(bTrimmed.length);
      } else if (bCode && cleanRoom.startsWith(bCode)) {
        cleanRoom = cleanRoom.substring(bCode.length);
      }

      // 2. If roomName still contains building code/name prefix before hyphen
      const lastDashIdx = cleanRoom.lastIndexOf("-");
      if (lastDashIdx >= 0) {
        const prefix = cleanRoom.substring(0, lastDashIdx).trim();
        if (
          (bCode && prefix.includes(bCode)) ||
          (bTrimmed && prefix.includes(bTrimmed)) ||
          (bAltName && prefix.includes(bAltName))
        ) {
          cleanRoom = cleanRoom.substring(lastDashIdx + 1);
        }
      }

      // Strip leading delimiters like "-", "/", ":", or whitespace
      cleanRoom = cleanRoom.replace(/^[-/:\s]+/, "").trim();

      // If room became empty after stripping, fallback to original rName
      if (!cleanRoom) {
        cleanRoom = rName;
      }

      if (typeof t === "function") {
        return t("ticketDetail.locationBuilding")
          .replace("{b}", locale ? getBuildingLocalizedName(bObj, locale) : bName)
          .replace("{r}", cleanRoom);
      }
      return `ตึก ${bName} / ห้อง ${cleanRoom}`;
    }

    const dashIdx = rName.indexOf("-");
    if (dashIdx > 0) {
      const parsedBuilding = rName.substring(0, dashIdx).trim();
      const parsedRoom = rName.substring(dashIdx + 1).trim();
      if (typeof t === "function") {
        return t("ticketDetail.locationBuilding")
          .replace("{b}", parsedBuilding)
          .replace("{r}", parsedRoom);
      }
      return `ตึก ${parsedBuilding} / ห้อง ${parsedRoom}`;
    }

    return rName;
  }

  if (u.locationType === "outdoor") {
    if (u.outdoorLocation) {
      const outdoorFallback = typeof t === "function" ? t("ticketDetail.outdoor") : "นอกอาคาร";
      return u.locationDetail || outdoorFallback;
    }
  }

  return u.locationDetail || "-";
}

/**
 * Returns formatted available time display string.
 * @param {Object} report
 * @param {string} [lang='th']
 * @param {Function} [t] - Translation function
 */
export function getTicketAvailableTime(report, lang = "th", t) {
  const raw = report && report.userPart && report.userPart.availableTime;
  if (!raw) return "-";
  const formatted = formatAvailableTimeDisplay(raw, lang);
  if (lang === "th") {
    return formatted.endsWith(" น.") || formatted.endsWith("\u00A0น.")
      ? formatted
      : `${formatted}\u00A0น.`;
  }
  return formatted;
}

/**
 * Formats an ISO date string into DD/MM/YYYY HH:mm with Buddhist/Gregorian year.
 * @param {string|Date} dateString
 * @param {string} [lang='th']
 */
export function formatTicketDate(dateString, lang = "th") {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "-";

  const loc = lang === "en" ? "en-GB" : "th-TH";
  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  if (lang === "en") {
    return d.toLocaleString(loc, options).replace(",", "");
  }

  const parts = d.toLocaleString(loc, options).split(" ");
  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1]}`;
  }
  return d.toLocaleString(loc, options);
}

/**
 * Returns reporter full name.
 */
export function getTicketUserName(report) {
  const user = report && report.userPart && report.userPart.user;
  if (!user || typeof user !== "object") return "-";
  return `${user.name || ""} ${user.surname || ""}`.trim() || "-";
}

/**
 * Returns reporter mobile / personal phone.
 */
export function getTicketPersonalPhone(report) {
  return (
    (report && report.userPart && report.userPart.personalPhone) ||
    (report && report.userPart && report.userPart.user && report.userPart.user.mobile_phone) ||
    "-"
  );
}

/**
 * Returns reporter work / office phone.
 */
export function getTicketOfficePhone(report) {
  return (
    (report && report.userPart && report.userPart.telephoneWork) ||
    (report && report.userPart && report.userPart.user && report.userPart.user.telephoneWork) ||
    "-"
  );
}

/**
 * Returns technician phone number(s).
 * Inspects supervisorPart assignments, assignedTechnician, technicianPart technician,
 * and falls back to profile or techniciansList if assignee is ID.
 * @param {Object} report
 * @param {Array} [techniciansList]
 * @param {Object} [profile]
 */
export function getTicketTechnicianPhone(report, techniciansList = [], profile = null) {
  if (!report) return "-";

  const phones = [];

  const extractPhone = (tech) => {
    if (!tech) return null;
    if (typeof tech === "object") {
      return (
        tech.mobile_phone ||
        tech.phone ||
        tech.tel ||
        tech.telephoneWork ||
        null
      );
    }
    if (typeof tech === "string" || typeof tech === "number") {
      const strId = String(tech);
      if (profile && String(profile._id || profile.id) === strId) {
        return (
          profile.mobile_phone ||
          profile.phone ||
          profile.tel ||
          profile.telephoneWork ||
          null
        );
      }
      if (Array.isArray(techniciansList)) {
        const found = techniciansList.find((t) => String(t._id || t.id) === strId);
        if (found) {
          return (
            found.mobile_phone ||
            found.phone ||
            found.tel ||
            found.telephoneWork ||
            null
          );
        }
      }
    }
    return null;
  };

  // 1. Check supervisorPart.assignments
  const assignments = Array.isArray(report?.supervisorPart?.assignments)
    ? report.supervisorPart.assignments
    : [];

  if (assignments.length > 0) {
    const accepted = assignments.filter((a) => a && a.status === "accepted");
    const active = assignments.filter((a) => a && a.status !== "rejected");
    const listToUse =
      ["done", "welldone", "in_progress"].includes(report?.status) && accepted.length > 0
        ? accepted
        : active.length > 0
        ? active
        : assignments;

    listToUse.forEach((a) => {
      const p = extractPhone(a.assignee);
      if (p) phones.push(p);
    });
  }

  // 2. Check supervisorPart.assignedTechnician
  if (phones.length === 0 && report?.supervisorPart?.assignedTechnician) {
    const p = extractPhone(report.supervisorPart.assignedTechnician);
    if (p) phones.push(p);
  }

  // 3. Check technicianPart.technician
  if (phones.length === 0 && report?.technicianPart?.technician) {
    const p = extractPhone(report.technicianPart.technician);
    if (p) phones.push(p);
  }

  // 4. Check technicianPart direct phone fields
  if (phones.length === 0) {
    const directPhone =
      report?.technicianPart?.mobile_phone ||
      report?.technicianPart?.phone ||
      report?.technicianPart?.tel;
    if (directPhone) phones.push(directPhone);
  }

  // 5. Fallback for technician view when assigned to logged in profile
  if (phones.length === 0 && profile && profile.role === "technician") {
    const p = extractPhone(profile);
    if (p) phones.push(p);
  }

  const validPhones = [
    ...new Set(
      phones
        .map((p) => String(p).trim())
        .filter((p) => p && p !== "-")
    ),
  ];

  return validPhones.length > 0 ? validPhones.join(", ") : "-";
}

/**
 * Returns reporter organization unit / affiliation.
 */
export function getTicketReporterAffiliation(report) {
  const user = report && report.userPart && report.userPart.user;
  // Preserve the existing source order, but every fallback must meet the same
  // verified Blendata contract as the canonical populated User affiliation.
  for (const affiliation of [user?.affiliation, report?.userPart?.affiliation, report?.affiliation]) {
    const name = getVerifiedBlendataAffiliationName(affiliation, "");
    if (name) return name;
  }
  return "-";
}

/**
 * Returns maintenance section name.
 * @param {Object} report
 * @param {string} [lang]
 */
export function getTicketSectionName(report, lang) {
  const cat = report && report.userPart && report.userPart.category;
  if (!cat) return "-";
  let raw = "-";
  if (typeof cat === "object") {
    raw = cat.name || cat.departmentName || cat.code || "-";
  } else {
    raw = String(cat);
  }
  return translateSection(typeof cat === "object" ? cat : raw, lang);
}

/**
 * Returns job type name.
 * @param {Object} report
 * @param {string} [lang]
 */
export function getTicketJobTypeName(report, lang) {
  const raw = (report && report.userPart && report.userPart.subCategory) || "-";
  const cat=report?.userPart?.category;
  const job=cat?.subCategories?.find(j=>(report?.userPart?.subCategoryId&&String(j._id)===String(report.userPart.subCategoryId))||j.name===raw||(j.previousNames||[]).includes(raw));
  return translateJobType(job||raw, lang);
}

/**
 * Returns maintenance department name if available from selected job type, otherwise "-".
 * Strictly resolves from the selected Job Type routing without falling back to staff departments.
 * Never substitutes Section as Department.
 * @param {Object} report
 * @param {Object} [departmentsMap]
 * @param {string} [lang]
 */
export function getTicketDepartmentName(report, departmentsMap = {}, lang) {
  if (!report) return "-";

  // Check if department is populated or resolvable on the selected subCategory
  const cat = report.userPart && report.userPart.category;
  const subCategoryName = report.userPart && report.userPart.subCategory;
  if (cat && Array.isArray(cat.subCategories) && subCategoryName) {
    const matchedSub = cat.subCategories.find(
      (s) => s && (s.name === subCategoryName || String(s._id) === String(subCategoryName))
    );
    if (matchedSub && matchedSub.department) {
      if (typeof matchedSub.department === "object" && matchedSub.department.name) {
        return translateDepartment(matchedSub.department, lang);
      }
      const deptId = String(matchedSub.department);
      if (departmentsMap && departmentsMap[deptId]) {
        return translateDepartment(departmentsMap[deptId], lang);
      }
    }
  }

  if (report.userPart && report.userPart.responsibleDepartment) {
    return translateDepartment(report.userPart.responsibleDepartment, lang);
  }

  return "-";
}

/**
 * Returns on behalf of text.
 */
export function getTicketOnBehalfOf(report) {
  return (report && report.onBehalfOf) || (report && report.userPart && report.userPart.onBehalfOf) || "";
}

/**
 * Resolves the effective ticket status from the perspective of a specific technician.
 * If multiple technicians are assigned to a ticket:
 * - If the technician's individual assignment status is 'pending' and ticket is not cancelled/done/welldone:
 *   The technician has not yet accepted the job, so it appears as 'assigned' (New Job) for them,
 *   even if another technician has already accepted causing ticket.status to be 'in_progress'.
 * - If the technician's assignment status is 'accepted':
 *   It is 'in_progress' (or current active status).
 * - If the ticket is done, welldone, or cancelled:
 *   Returns the ticket's final status.
 *
 * @param {Object} ticket
 * @param {string|number} [technicianId] - Logged-in technician's user ID
 * @returns {string} Effective status for the technician
 */
export function getTechnicianEffectiveStatus(ticket, technicianId) {
  if (!ticket) return "pending";
  const globalStatus = ticket.status || "pending";

  if (["done", "welldone", "cancelled", "cancel"].includes(globalStatus)) {
    return globalStatus;
  }

  if (!technicianId) {
    return globalStatus;
  }

  const tid = String(technicianId);
  const assignments = ticket.supervisorPart?.assignments;

  if (Array.isArray(assignments) && assignments.length > 0) {
    const myAssignment = assignments.find((a) => {
      const aId = a.assignee?._id ? a.assignee._id : a.assignee;
      return String(aId || "") === tid;
    });

    if (myAssignment) {
      if (myAssignment.status === "rejected") {
        return "rejected";
      }
      if (myAssignment.status === "pending") {
        return "assigned";
      }
      if (myAssignment.status === "accepted") {
        return globalStatus === "in_progress" ? "in_progress" : globalStatus;
      }
    }
  }

  const legacyTech = ticket.supervisorPart?.assignedTechnician;
  const legacyTechId = legacyTech?._id ? legacyTech._id : legacyTech;
  if (legacyTechId && String(legacyTechId) === tid) {
    return globalStatus;
  }

  return globalStatus;
}

/**
 * Checks whether the ticket is awaiting acceptance by the specified technician.
 * @param {Object} ticket
 * @param {string|number} [technicianId]
 * @returns {boolean}
 */
export function isTechnicianPendingAcceptance(ticket, technicianId) {
  if (!ticket || !technicianId) return false;
  if (["done", "welldone", "cancelled", "cancel"].includes(ticket.status)) return false;
  const tid = String(technicianId);
  const assignments = ticket.supervisorPart?.assignments;
  if (Array.isArray(assignments) && assignments.length > 0) {
    const myAssignment = assignments.find((a) => {
      const aId = a.assignee?._id ? a.assignee._id : a.assignee;
      return String(aId || "") === tid;
    });
    if (myAssignment) {
      return myAssignment.status === "pending";
    }
  }
  return ticket.status === "assigned";
}

import { getVerifiedBlendataAffiliationName } from "./affiliation";
